import { type ReactNode, useMemo, useState } from "react";
import { CalendarClock, ClipboardList, Pencil, Plane, Plus, Save, Trash2, X } from "lucide-react";
import { occurrenceSelectorId, rotationLegSelectorId } from "../../../domain/data/scheduleQueries";
import type { PlayerRotation, SaveGame, ScheduleSource } from "../../../domain/types";
import type { RotationDraft } from "../../gameSession";
import { PanelHeader } from "../../components/Panel";
import type { ScheduledFlightGroup } from "./playerTypes";
import { groupScheduledFlights, rotationContinuity, rotationPathLabel, rotationServiceDaysLabel } from "./playerData";


export function PlanningView(props: {
  save: SaveGame;
  schedule: ScheduleSource;
  draft: RotationDraft;
  setDraft: (value: RotationDraft) => void;
  onSaveRotation: (input: RotationDraft) => void;
  onDeleteRotation: (rotationId: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const needle = filter.trim().toUpperCase();
  const draftScheduledFlightIds = draftSelectorIds(props.draft, props.schedule);
  const occupiedSelectorIds = new Set(
    props.save.rotations
      .filter((rotation) => rotation.id !== props.draft.id)
      .flatMap((rotation) => rotation.legs.map((leg) => rotationLegSelectorId(leg)).filter(Boolean))
  );
  const scheduledFlightGroups = useMemo(() => groupScheduledFlights(props.schedule.flightPlans), [props.schedule.flightPlans]);
  const visibleFlightGroups = scheduledFlightGroups.filter((group) => {
    if (occupiedSelectorIds.has(group.id)) return false;
    if (!needle) return true;
    return [group.flightNo, group.origin, group.destination, group.serviceDaysLabel, group.departureTimeLocal].some((value) => value.toUpperCase().includes(needle));
  });
  const selectedFlightGroups = draftScheduledFlightIds
    .map((id) => scheduledFlightGroups.find((group) => group.id === id))
    .filter(Boolean) as ScheduledFlightGroup[];
  const toggleScheduledFlight = (scheduledFlightId: string) => {
    const scheduledFlightIds = toggledScheduledFlightIds(draftScheduledFlightIds, scheduledFlightId);
    props.setDraft({ ...props.draft, scheduledFlightIds, occurrenceIds: [] });
  };
  const moveScheduledFlight = (scheduledFlightId: string, direction: -1 | 1) => {
    const index = draftScheduledFlightIds.indexOf(scheduledFlightId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= draftScheduledFlightIds.length) return;
    const next = [...draftScheduledFlightIds];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    props.setDraft({ ...props.draft, scheduledFlightIds: next, occurrenceIds: [] });
  };
  const continuity = rotationContinuity(selectedFlightGroups);
  const resetDraft = () => props.setDraft({ name: "新航班串", occurrenceIds: [], scheduledFlightIds: [] });
  const editRotation = (rotation: PlayerRotation) => {
    const scheduledFlightIds = [...rotation.legs]
      .sort((a, b) => a.sequence - b.sequence)
      .map((leg) => rotationLegSelectorId(leg))
      .filter((id): id is string => Boolean(id));
    props.setDraft({
      id: rotation.id,
      name: rotation.name,
      occurrenceIds: [],
      scheduledFlightIds
    });
  };
  const isEditing = Boolean(props.draft.id);
  return (
    <section className="player-view planning-layout">
      <section className="panel planning-pool-panel">
        <PanelHeader icon={<ClipboardList size={15} />} title="GM 定期航班池" code="POOL" />
        <label className="search-line">
          筛选
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="航班号 / 机场" />
        </label>
        <div className="table-wrap compact">
          <table>
            <thead>
              <tr>
                <th>选择</th>
                <th>航班</th>
                <th>航段</th>
                <th>服务日</th>
                <th>时刻</th>
              </tr>
            </thead>
            <tbody>
              {visibleFlightGroups.map((group) => (
                <tr key={group.id}>
                  <td>
                    <input type="checkbox" checked={draftScheduledFlightIds.includes(group.id)} onChange={() => toggleScheduledFlight(group.id)} />
                  </td>
                  <td>{group.flightNo}</td>
                  <td>{group.origin}-{group.destination}</td>
                  <td>{group.serviceDaysLabel}</td>
                  <td>{group.departureTimeLocal}</td>
                </tr>
              ))}
              {visibleFlightGroups.length === 0 && (
                <tr>
                  <td colSpan={5}>没有可编组的 GM 定期航班。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel planning-builder-panel">
        <PanelHeader icon={<Plane size={15} />} title={planningPanelTitle(isEditing)} code="MAKE" />
        <div className="planner-grid">
          <label>
            航班串名称
            <input value={props.draft.name} onChange={(event) => props.setDraft({ ...props.draft, name: event.target.value })} />
          </label>
          <div className="form-readout">航班串按 GM 定期航班自动匹配未来具体班次；玩家这里只定义规则顺序，运行日按始发站自动挑选可用飞机。</div>
          <div className={`form-readout ${continuityReadoutClassName(continuity.ok)}`}>{continuity.message}</div>
          <div className="builder-actions">
            <button className="primary-command" disabled={selectedFlightGroups.length === 0 || !continuity.ok} onClick={() => props.onSaveRotation({ ...props.draft, scheduledFlightIds: draftScheduledFlightIds, occurrenceIds: [] })}>
              {planningSaveIcon(isEditing)}
              {planningSaveLabel(isEditing)}
            </button>
            <button className="command" disabled={!isEditing} onClick={resetDraft}>
              <X size={14} />
              新建
            </button>
          </div>
        </div>
        <div className="selected-flight-list">
          {selectedFlightGroups.map((group, index) => (
            <article key={group.id} className="selected-flight-card">
              <strong>{index + 1}. {group.flightNo}</strong>
              <span>{group.origin}-{group.destination} / {group.serviceDaysLabel} / {group.departureTimeLocal}</span>
              <div className="row-actions">
                <button disabled={index === 0} onClick={() => moveScheduledFlight(group.id, -1)}>上移</button>
                <button disabled={index === selectedFlightGroups.length - 1} onClick={() => moveScheduledFlight(group.id, 1)}>下移</button>
                <button onClick={() => toggleScheduledFlight(group.id)}>移除</button>
              </div>
            </article>
          ))}
          {selectedFlightGroups.length === 0 && <div className="empty-state">从左侧航班池选择 GM 定期航班后，按顺序保存为自动航班串规则。</div>}
        </div>
      </section>

      <section className="panel planning-existing-panel">
        <PanelHeader icon={<CalendarClock size={15} />} title="已保存玩家航班串" code="OWN" />
        <SavedRotationTable
          rotations={props.save.rotations}
          schedule={props.schedule}
          activeRotationId={props.draft.id}
          locked={Boolean(props.save.activeOpsDay)}
          onEdit={editRotation}
          onDelete={props.onDeleteRotation}
        />
      </section>
    </section>
  );
}

function SavedRotationTable({
  rotations,
  schedule,
  activeRotationId,
  locked,
  onEdit,
  onDelete
}: {
  rotations: PlayerRotation[];
  schedule: ScheduleSource;
  activeRotationId?: string;
  locked: boolean;
  onEdit: (rotation: PlayerRotation) => void;
  onDelete: (rotationId: string) => void;
}) {
  return (
    <div className="table-wrap compact">
      <table>
        <thead>
          <tr>
            <th>名称</th>
            <th>服务日</th>
            <th>航段链</th>
            <th>段数</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rotations.map((rotation) => (
            <tr key={rotation.id} className={savedRotationRowClassName(activeRotationId, rotation.id)}>
              <td>{rotation.name}</td>
              <td>{rotationServiceDaysLabel(rotation, schedule)}</td>
              <td>{rotationPathLabel(rotation, schedule)}</td>
              <td>{rotation.legs.length} / 始发站自动择机</td>
              <td>
                <div className="row-actions">
                  <button onClick={() => onEdit(rotation)}>
                    <Pencil size={12} />
                    编辑
                  </button>
                  <button disabled={locked} title={deleteRotationTitle(locked)} onClick={() => onDelete(rotation.id)}>
                    <Trash2 size={12} />
                    删除
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {rotations.length === 0 && (
            <tr>
              <td colSpan={5}>暂无保存的玩家航班串。</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function draftSelectorIds(draft: RotationDraft, schedule: ScheduleSource): string[] {
  if (draft.scheduledFlightIds.length > 0) {
    return draft.scheduledFlightIds;
  }
  return draft.occurrenceIds.map((id) => occurrenceSelectorId(id, schedule));
}

function toggledScheduledFlightIds(selectedIds: string[], scheduledFlightId: string): string[] {
  if (selectedIds.includes(scheduledFlightId)) {
    return selectedIds.filter((id) => id !== scheduledFlightId);
  }
  return [...selectedIds, scheduledFlightId];
}

function planningPanelTitle(isEditing: boolean): string {
  if (isEditing) {
    return "编辑玩家航班串";
  }
  return "玩家航班串编组";
}

function continuityReadoutClassName(ok: boolean): string {
  if (ok) {
    return "";
  }
  return "bad";
}

function planningSaveIcon(isEditing: boolean): ReactNode {
  if (isEditing) {
    return <Save size={15}/>;
  }
  return <Plus size={15}/>;
}

function planningSaveLabel(isEditing: boolean): string {
  if (isEditing) {
    return "更新航班串";
  }
  return "保存航班串";
}

function savedRotationRowClassName(activeRotationId: string | undefined, rotationId: string): string {
  if (activeRotationId === rotationId) {
    return "selected-row";
  }
  return "";
}

function deleteRotationTitle(locked: boolean): string {
  if (locked) {
    return "运行日开放时不能删除航班串";
  }
  return "删除航班串";
}
